import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { ScrollText, Search, Loader2, Shield, FileSpreadsheet, FileText } from "lucide-react";
import { exportExcel, exportPDF } from "../lib/exporters";

const ACTION_STYLE = (a) => {
  if (a.includes("delete") || a.includes("revoked") || a.includes("deactivated")) return "text-critical bg-critical/10";
  if (a.includes("active") || a.includes("create") || a.includes("activated")) return "text-calm bg-calm/10";
  return "text-clinical bg-clinical/10";
};

export function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ date_from: "", date_to: "", role: "", action: "", entity_type: "", q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.get("/admin/audit-logs", { params });
      setLogs(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const inp = "border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";
  const AUDIT_COLS = [
    { key: "when", label: "When" }, { key: "user_name", label: "Who" }, { key: "role", label: "Role" },
    { key: "action", label: "Action" }, { key: "entity_type", label: "Entity" }, { key: "entity_label", label: "Detail" },
  ];
  const auditRows = () => logs.map((l) => ({ ...l, when: new Date(l.timestamp).toLocaleString(), role: (l.role || "").replace("_", " ") }));
  return (
    <div data-testid="audit-page">
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink mb-2 flex items-center gap-2"><ScrollText className="w-7 h-7 text-clinical" /> Audit Log</h1>
      <p className="text-muted2 text-sm mb-6 flex items-center gap-1.5"><Shield className="w-4 h-4" /> Read-only record of administrative actions.</p>

      <div className="bg-white border border-line rounded-md p-4 mb-5 flex flex-wrap gap-3 items-end" data-testid="audit-filters">
        <label className="text-xs font-bold text-muted2 uppercase">From<input type="date" className={`${inp} block mt-1`} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="audit-from" /></label>
        <label className="text-xs font-bold text-muted2 uppercase">To<input type="date" className={`${inp} block mt-1`} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="audit-to" /></label>
        <select className={inp} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="audit-role">
          <option value="">All roles</option><option value="system_admin">System Admin</option><option value="super_admin">Super Admin</option><option value="admin">Administrator</option>
        </select>
        <select className={inp} value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })} data-testid="audit-entity">
          <option value="">All entities</option><option value="user">User</option><option value="banner">Banner</option><option value="settings">Settings</option>
        </select>
        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted2" />
          <input className={`${inp} w-full pl-8`} placeholder="Search…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="audit-search" />
        </div>
        <button onClick={load} data-testid="audit-apply-btn" className="bg-clinical text-white rounded-md px-4 py-2 text-sm font-semibold">Apply</button>
        <button onClick={() => exportExcel("audit_logs", AUDIT_COLS, auditRows())} disabled={!logs.length} data-testid="audit-export-excel" className="inline-flex items-center gap-1.5 border border-line rounded-md px-3 py-2 text-sm font-semibold text-calm disabled:opacity-40"><FileSpreadsheet className="w-4 h-4" /> Excel</button>
        <button onClick={() => exportPDF("Audit Logs", AUDIT_COLS, auditRows())} disabled={!logs.length} data-testid="audit-export-pdf" className="inline-flex items-center gap-1.5 border border-line rounded-md px-3 py-2 text-sm font-semibold text-critical disabled:opacity-40"><FileText className="w-4 h-4" /> PDF</button>
      </div>

      {loading ? (
        <p className="text-muted2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : logs.length === 0 ? (
        <p className="text-muted2">No matching log entries.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-line rounded-md">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted2 border-b border-line">
                <th className="px-4 py-3">When</th><th className="px-4 py-3">Who</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} data-testid="audit-row" className="border-b border-line/60 last:border-0">
                  <td className="px-4 py-3 text-muted2 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3 font-medium text-ink">{l.user_name}</td>
                  <td className="px-4 py-3 text-muted2">{(l.role || "").replace("_", " ")}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-0.5 rounded ${ACTION_STYLE(l.action)}`}>{l.action}</span></td>
                  <td className="px-4 py-3 text-muted2">{l.entity_type}{l.entity_label ? ` · ${l.entity_label}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
